import { hideVisually } from "polished";
import * as React from "react";
import styled, { css } from "styled-components";
import theme from "../../../theme";

export interface IToggleButtonOption {
  description?: string;
  label: string;
  value: string;
}

export interface IToggleButtonGroupProps {
  label: string;
  onChange: (value: string) => void;
  options: readonly IToggleButtonOption[];
  value?: string;
}

interface IToggleButtonLabelProps {
  $checked?: boolean;
}

const ToggleGroupTitle = styled.span`
  display: block;
  margin-bottom: ${theme.space[1]};
  color: ${theme.colors.white};
  font-size: 1.5rem;
  font-weight: bold;
  cursor: pointer;
`;

const ToggleButtonGroupWrap = styled.div`
  display: block;
  margin: 2rem 0;
`;

const ToggleButtonRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${theme.space[2]};
`;

const ToggleButtonLabel = styled.label<IToggleButtonLabelProps>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: ${theme.space[2]} 0;
  border: ${theme.borders[0]};
  border-radius: ${theme.radii[1]};
  background-color: ${theme.colors.lighterBlue};
  color: ${theme.colors.blueDarker4};
  cursor: pointer;
  flex: 1 1 auto;

  &:focus-within {
    outline: 1px solid ${theme.colors.lighterBlue};
    outline-offset: 1px;
  }

  ${(props) =>
    props.$checked &&
    css`
      border-color: ${theme.colors.black};
      background-color: ${theme.colors.blueDarker4};
      color: ${theme.colors.white};
      filter: drop-shadow(2px 2px 6px rgba(175, 178, 250, 0.95));
    `}
`;

const ToggleButtonInput = styled.input`
  ${hideVisually()};
`;

const ToggleButtonText = styled.span`
  font-family: ${theme.fonts.base};
  font-size: 16px;
  font-weight: 700;
`;

export const ToggleButtonGroup: React.FC<IToggleButtonGroupProps> = (props) => {
  const { label, onChange, options, value } = props;

  if (options.length === 0) {
    return null;
  }

  return (
    <ToggleButtonGroupWrap role="radiogroup" aria-label={label}>
      <ToggleGroupTitle>{label}</ToggleGroupTitle>
      <ToggleButtonRow>
        {options.map((option) => {
          const checked = option.value === value;
          return (
            <ToggleButtonLabel key={option.value} $checked={checked}>
              <ToggleButtonInput
                type="radio"
                value={option.value}
                checked={checked}
                onChange={() => onChange(option.value)}
                onClick={() => onChange(option.value)}
              />
              <ToggleButtonText>{option.label}</ToggleButtonText>
            </ToggleButtonLabel>
          );
        })}
      </ToggleButtonRow>
    </ToggleButtonGroupWrap>
  );
};
